import React from "react";
import {FormProvider, useForm, useWatch} from "react-hook-form";
import {Button, Paper, Tab, Tabs, Typography, Grid} from "@mui/material";
import {FormInputText} from "../../shop/wholesale/form/FormInputText";
import {FormInputSelect} from "../../shop/wholesale/form/FormInputSelect";
import {FormInputSelectMultiple} from "../../shop/wholesale/form/FormInputSelectMultiple";
import {FormInputCheckbox} from "../../shop/wholesale/form/FormInputCheckbox";
import {ShipmondoEditor} from "./ShipmondoEditor";
import {CarrierGatewayEditor} from "./CarrierGatewayEditor";
import {EnumShippingCarrierCarrier, EnumShippingCarrierCarrierGatewayInputCarrier, EnumShippingCarrierCarrierGatewayOutputCarrier} from "../../../Queries";

export enum CarrierEnum {
   shipmondo = 'shipmondo',
   pickup = 'pickup',
   carriergateway = 'carriergateway'
}

export interface ShippingCarrierFormData {
   _id?: string;
   logo?: string;
   name: string;
   carrier?: EnumShippingCarrierCarrier;
   carrierId: CarrierEnum;
   description: string;
   externalName: string;
   externalCode: string;
   expression?: string;
   activeFor: string[];
   localizationKey?: string;
   shipmondo?: {
      ownAgreement?: boolean;
      testMode?: boolean;
      carrier?: string;
      carrierSecondary?: string;
      carrierCode?: string;
      serviceCodes?: string;
      customerNumber?: string;
      qrX?: string;
      qrY?: string;
      qrSize?: number;
      width?: number;
      height?: number;
      length?: number;
      description?: string;
      customs?: string;
   };
   carrierGateway?: {
      apiKey: string;
      inputCarrier: EnumShippingCarrierCarrierGatewayInputCarrier;
      outputCarrier: EnumShippingCarrierCarrierGatewayOutputCarrier;
      supportQR: boolean;
   };
   customCarrier?: boolean;
}

interface ShippingCarrierFormProps {
   initialData?: ShippingCarrierFormData;
   brands: Array<{_id: any; name?: string | null}>;
   onSubmit: (data: ShippingCarrierFormData) => Promise<void>;
}

export const ShippingCarrierForm: React.FC<ShippingCarrierFormProps> = ({
   initialData,
   brands,
   onSubmit
}) => {
   const methods = useForm<ShippingCarrierFormData>({
      defaultValues: {
         name: '',
         carrierId: CarrierEnum.shipmondo,
         description: '',
         externalName: '',
         externalCode: '',
         expression: '',
         activeFor: [],
         localizationKey: '',
         customCarrier: false,
         ...initialData
      }
   });

   const { handleSubmit, control, setValue } = methods;
   const carrierId = useWatch({ control, name: 'carrierId' });

   const brandOptions = brands.map(brand => ({
      id: brand._id,
      label: brand.name || 'Unknown Brand'
   }));

   const carrierOptions = Object.values(EnumShippingCarrierCarrier).map(value => ({
      id: value,
      name: value
   }));


   return (
      <FormProvider {...methods}>
         <form onSubmit={handleSubmit(onSubmit)}>
            <Typography variant="h1" gutterBottom>
               Shipping Carrier
            </Typography>

            <Grid container spacing={3}>
               <Grid item xs={12} md={6}>
                  <FormInputText name="name" label="Name" />
               </Grid>
               <Grid item xs={12} md={6}>
                  <FormInputText name="localizationKey" label="Localization identifier" />
               </Grid>
            </Grid>

            <Grid container spacing={3}>
               <Grid item xs={12}>
                  <FormInputText name="description" label="Description (Brands will see this)" />
               </Grid>
            </Grid>

            <Grid container spacing={3}>
               <Grid item xs={12} md={6}>
                  <FormInputText name="externalName" label="Name of carrier, as it appears for end consumers" />
               </Grid>
               <Grid item xs={12} md={6}>
                  <FormInputText name="externalCode" label="Short hand name of carrier, as it appears for end consumers" />
               </Grid>
            </Grid>

            <Grid container spacing={3}>
               <Grid item xs={12} md={6}>
                  <FormInputSelect name="carrier" label="Carrier" options={carrierOptions} />
               </Grid>
            </Grid>

            <Grid container spacing={3}>
               <Grid item xs={12}>
                  <FormInputText name="expression" label="Visibility expression (Only allow this carrier if the expression evaluates to true)" />
               </Grid>
            </Grid>

            <Grid container spacing={3}>
               <Grid item xs={12} md={6}>
                  <FormInputSelectMultiple 
                     name="activeFor" 
                     label="Active For"
                     options={brandOptions}
                  />
               </Grid>
               <Grid item xs={12} md={6}>
                  <FormInputCheckbox name="customCarrier" label="Enable Custom Carrier" />
               </Grid>
            </Grid>

            <div style={{marginTop: '20px'}}>
               <Tabs value={carrierId}>
                  <Tab 
                     value={CarrierEnum.shipmondo} 
                     label="Shipmondo" 
                     onClick={() => setValue('carrierId', CarrierEnum.shipmondo)}
                  />
                  <Tab 
                     value={CarrierEnum.carriergateway} 
                     label="Carrier Gateway" 
                     onClick={() => setValue('carrierId', CarrierEnum.carriergateway)}
                  />
                  <Tab 
                     value={CarrierEnum.pickup} 
                     label="Pickup" 
                     onClick={() => setValue('carrierId', CarrierEnum.pickup)}
                  />
               </Tabs>
               
               <Paper variant="outlined" style={{padding: '20px', marginTop: '10px'}}>
                  {carrierId === CarrierEnum.shipmondo && <ShipmondoEditor control={control}/>}
                  {carrierId === CarrierEnum.carriergateway && <CarrierGatewayEditor control={control}/>}
                  {carrierId === CarrierEnum.pickup && (
                     <Typography>Pickup carrier - no additional configuration needed</Typography>
                  )}
               </Paper>
            </div>

            <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '20px'}}>
               <Button type="submit" variant="contained">
                  Update
               </Button>
            </div>
         </form>
      </FormProvider>
   );
};